import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  BriefcaseBusiness,
  CircleDollarSign,
  Database,
  FolderOpen,
  LineChart,
  ListTodo,
  Table2,
} from "lucide-react";

export interface ProjectAccent {
  name: string;
  dot: string;
  bg: string;
  softBg: string;
  border: string;
  text: string;
  ring: string;
}

export interface ProjectVisuals {
  accent: ProjectAccent;
  Icon: LucideIcon;
  tags: string[];
}

const defaultProjectAccent: ProjectAccent = {
  name: "default",
  dot: "bg-visagio-teal",
  bg: "bg-visagio-teal",
  softBg: "bg-visagio-teal/10",
  border: "border-visagio-teal/20",
  text: "text-visagio-teal",
  ring: "ring-visagio-teal/20",
};

export const projectAccents: ProjectAccent[] = [
  {
    name: "blue",
    dot: "bg-origem-blue",
    bg: "bg-origem-blue",
    softBg: "bg-origem-blue/10 dark:bg-origem-blue/25",
    border: "border-origem-blue/25 dark:border-origem-blue/40",
    text: "text-origem-blue dark:text-origem-sky",
    ring: "ring-origem-blue/20",
  },
  {
    name: "royal",
    dot: "bg-origem-royal",
    bg: "bg-origem-royal",
    softBg: "bg-origem-royal/10 dark:bg-origem-royal/25",
    border: "border-origem-royal/25 dark:border-origem-royal/40",
    text: "text-origem-royal dark:text-origem-sky",
    ring: "ring-origem-royal/20",
  },
  {
    name: "navy",
    dot: "bg-origem-navy",
    bg: "bg-origem-navy",
    softBg: "bg-origem-sky/35 dark:bg-origem-navy/40",
    border: "border-origem-navy/20 dark:border-origem-sky/30",
    text: "text-origem-navy dark:text-origem-sky",
    ring: "ring-origem-navy/20",
  },
  {
    name: "sky",
    dot: "bg-origem-sky",
    bg: "bg-origem-sky",
    softBg: "bg-origem-sky/40 dark:bg-origem-sky/20",
    border: "border-origem-sky dark:border-origem-sky/50",
    text: "text-origem-navy dark:text-origem-sky",
    ring: "ring-origem-sky/30",
  },
  {
    name: "taupe",
    dot: "bg-origem-taupe",
    bg: "bg-origem-taupe",
    softBg: "bg-origem-taupe/50 dark:bg-origem-taupe/20",
    border: "border-origem-brown/30 dark:border-origem-taupe/40",
    text: "text-origem-brown dark:text-origem-taupe",
    ring: "ring-origem-taupe/30",
  },
  {
    name: "brown",
    dot: "bg-origem-brown",
    bg: "bg-origem-brown",
    softBg: "bg-origem-brown/15 dark:bg-origem-brown/25",
    border: "border-origem-brown/30 dark:border-origem-brown/45",
    text: "text-origem-brown dark:text-origem-taupe",
    ring: "ring-origem-brown/20",
  },
];

const legacyAccentAliases: Record<string, string> = {
  primary: "royal",
  accent: "brown",
  secondary: "taupe",
  brand: "royal",
  highlight: "sky",
  neutral: "navy",
  // Back-compatibility with default project colors defined by Visagio.
  teal: "blue",
  emerald: "sky",
  sky: "sky",
  amber: "taupe",
  rose: "brown",
  indigo: "navy",
};

export const DEFAULT_TAG_KEYS = [
  "production", "drilling", "intervention", "wellTesting", "planning", "analysis",
] as const;
export type DefaultTagKey = typeof DEFAULT_TAG_KEYS[number];

export function displayTag(tag: string, t: (key: string) => string): string {
  return (DEFAULT_TAG_KEYS as readonly string[]).includes(tag)
    ? t(`defaultTags.${tag}`)
    : tag;
}

export const projectIconOptions: Array<{ key: string; Icon: LucideIcon }> = [
  { key: "folder", Icon: FolderOpen },
  { key: "database", Icon: Database },
  { key: "bar-chart", Icon: BarChart3 },
  { key: "table", Icon: Table2 },
  { key: "line-chart", Icon: LineChart },
  { key: "briefcase", Icon: BriefcaseBusiness },
  { key: "finance", Icon: CircleDollarSign },
  { key: "list-todo", Icon: ListTodo },
];


export function getProjectVisuals(projectId: string, projectName = ""): ProjectVisuals {
  return {
    accent: defaultProjectAccent,
    Icon: FolderOpen,
    tags: [],
  };
}

export function resolveProjectVisuals(
  projectId: string,
  projectName = "",
  metadata?: {
    color?: string | null;
    icon?: string | null;
    tags?: string[] | null;
  }
): ProjectVisuals {
  const fallback = getProjectVisuals(projectId, projectName);
  const color = metadata?.color ? legacyAccentAliases[metadata.color] || metadata.color : undefined;
  const accent = projectAccents.find((item) => item.name === color) || fallback.accent;
  const Icon = projectIconOptions.find((item) => item.key === metadata?.icon)?.Icon || fallback.Icon;
  const tags = metadata?.tags?.filter(Boolean).slice(0, 6) ?? [];

  return {
    accent,
    Icon,
    tags,
  };
}
